import { FieldWrapper } from "components/ui/FieldWrapper";
import { Textarea, type TextareaProps } from "components/ui/Textarea";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

export type RhfTextareaProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control?: Control<T>;
  label?: string;
  required?: boolean;
  hint?: string;
} & Omit<TextareaProps, "tone">;

/**
 * React Hook Form アダプタ。JSX は ui/Textarea と ui/FieldWrapper に完全委譲する。
 * @param {RhfTextareaProps<T>} props RhfTextarea の props
 * @return {JSX.Element} FieldWrapper で包まれた Textarea
 */
export function RhfTextarea<T extends FieldValues>({
  name,
  control,
  label,
  required,
  hint,
  ...textareaProps
}: RhfTextareaProps<T>): JSX.Element {
  const { field, fieldState } = useController<T>({ name, control });

  return (
    <FieldWrapper
      label={label}
      required={required}
      hint={hint}
      error={fieldState.error?.message}
    >
      <Textarea
        {...textareaProps}
        name={field.name}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        ref={field.ref}
        tone={fieldState.error ? "invalid" : "default"}
      />
    </FieldWrapper>
  );
}
